import NextLink from 'next/link';

import { Faq as FaqType } from 'libs/http/api/content/content.types';

import { useInt } from 'hooks/useInt';

import { Faq } from 'ui/atoms/Faq/Faq';
import { Section } from 'ui/molecules/Section/Section';

import styles from './ManufacturingFaq.module.scss';

type Translate = (key: string) => string;

const getItems = (t: Translate): FaqType[] => [
  {
    id: 'source',
    default_opened: true,
    question: t('Where does the data come from?'),
    answer: t(
      'Primary filings held by government company registries, read directly from the register of record rather than bought from an aggregator. Every field we return carries <b>the register it came from and the date it was filed</b>, so a supplier decision can be reconstructed later by someone who was not in the room.',
    ),
  },
  {
    id: 'coverage',
    question: t('Which registries do you cover for our supply base?'),
    answer: t(
      'Coverage spans <b>400+ government registries across 200+ countries</b>, but disclosure is not uniform. Some registers publish shareholders and filed accounts, others publish identity and status only. The data dictionary sets out what is returned per jurisdiction, and we will run a sample of your own vendor file so you see the answer against your countries rather than against ours.',
    ),
  },
  {
    id: 'match',
    question: t('How do you match our vendor master to registry records?'),
    answer: t(
      'Records are matched on name, address, identifiers and jurisdiction, then resolved to a registration number. Trading names, transliterations, abbreviations and stale addresses collapse onto the same entity. The file comes back with the registration number, the register of record, <b>a match confidence and a duplicate flag</b> on every row.',
    ),
  },
  {
    id: 'rate',
    question: t('What match rate should we expect?'),
    answer: t(
      'It depends on the quality of the file and the jurisdictions in it, so we do not publish a single figure. Send <b>a few thousand rows weighted to the countries you actually source from</b> and we will report the rate against your data before any commercial conversation.',
    ),
  },
  {
    id: 'freshness',
    question: t('How often is the data updated?'),
    answer: t(
      'Registries are synced daily where the source publishes daily. Some registers update on filing cycles rather than continuously, so freshness follows the register rather than our schedule. <b>Each value carries its filing date</b>, which is the honest measure of how current it is.',
    ),
  },
  {
    id: 'ownership',
    question: t('Can you see ownership above our tier-one supplier?'),
    answer: t(
      'Yes. We return shareholders and percentages, the immediate parent, the ultimate parent, subsidiaries, and <b>beneficial owners where the register discloses them</b>. Traversal depth varies by jurisdiction because disclosure does, and the chain is returned with the register behind each step.',
    ),
  },
  {
    id: 'screening',
    question: t('Do you screen suppliers against sanctions and restricted-party lists?'),
    answer: t(
      'Screening runs against the resolved registry identity rather than the name printed on the invoice, at entity, officer and owner level. Results name <b>the source list and the version date screened against</b>, which is what an examiner asks for when the check is reviewed after the fact.',
    ),
  },
  {
    id: 'erp',
    question: t('How does this reach SAP, Oracle or Coupa?'),
    answer: t(
      'Through the API or a bulk file. Vendor records are matched in batch and returned with <b>registration numbers, group linkage and duplicate flags</b> for loading into the vendor master. Salesforce, HubSpot and Microsoft Dynamics have direct connectors that write registry fields back to the account record on a schedule.',
    ),
  },
  {
    id: 'watch',
    question: t('Can we monitor suppliers continuously rather than checking once at onboarding?'),
    answer: t(
      'Yes. A supplier placed under monitoring returns dated events rather than a score that quietly re-rates: <b>status changes, officer appointments and resignations, shareholding transfers, insolvency and charge filings, address and name changes</b>. Events are delivered to a webhook or held in a portfolio in the platform.',
    ),
  },
  {
    id: 'company',
    question: t('Who holds the data, and what certifications do you have?'),
    answer: t(
      "Global Data Intelligence Limited, company number 09410808, registered at Artisans' House, 7 Queensbridge, Northampton NN4 7BF. The company is <b>ISO 27001 certified</b> and <b>registered with the UK Information Commissioner's Office</b>.",
    ),
  },
];

export const ManufacturingFaq = () => {
  const { t } = useInt();

  return (
    <Section className={styles.root}>
      <Faq
        title={t('What procurement asks first.')}
        description={t(
          'If your question is not here, send a sample of your vendor file and we will answer it against your own data.',
        )}
        data={getItems(t)}
      />

      <div className={styles.footer}>
        <NextLink className={styles.contact} href="/contact-us">
          <span className={styles.contactIcon}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 5.5h16v13H4z" />
              <path d="M4.5 6l7.5 6.5L19.5 6" />
            </svg>
          </span>
          <span className={styles.contactLabel}>{t('Ask us directly')}</span>
          <span className={styles.contactArrow} aria-hidden="true">
            →
          </span>
        </NextLink>
      </div>
    </Section>
  );
};
